import { useState, useEffect, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AlertCircleIcon, CheckCircle2Icon, XCircleIcon } from 'lucide-react'
import {
	Alert, AlertDescription,
	Badge,
	Button,
	Checkbox,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	Input, Label,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
	Spinner,
	Tabs, TabsList, TabsTrigger, TabsContent,
} from '@rentflowdev/ui'
import {
	propertiesApi,
	useCompleteImportMutation,
	useGetImportQuery,
	useGetMeQuery,
	useImportAvitoItemsMutation,
	useListAvitoItemsQuery,
	useListSubAccountsQuery,
	useStartImportMutation,
} from '@/api'
import type { PublicationImport } from '@/types'
import { CREATE_PROPERTY_TYPES } from '@/types'

interface AvitoImportDialogProps {
	connectionId?: string
	open:          boolean
	onOpenChange:  (open: boolean) => void
}

// ── Cabinet tab ──────────────────────────────────────────────────────────────

function CabinetTab({ connectionId, open, onClose }: { connectionId: string; open: boolean; onClose: () => void }) {
	const { t } = useTranslation()
	const dispatch = useDispatch()
	const { data, isLoading, isError } = useListAvitoItemsQuery(
		{ connectionId, limit: 99 },
		{ skip: !open },
	)
	const [importItems, { isLoading: isImporting }] = useImportAvitoItemsMutation()
	const [selected, setSelected] = useState<Set<string>>(new Set())

	const items = data?.items ?? []

	function toggle(id: string) {
		setSelected((prev) => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	}

	async function handleImport() {
		if (selected.size === 0) return
		try {
			const res = await importItems({ connectionId, avitoItemIds: [...selected] }).unwrap()
			setSelected(new Set())
			if (res.imported.length > 0) {
				dispatch(propertiesApi.util.invalidateTags([{ type: 'Property', id: 'LIST' }]))
				onClose()
			}
		} catch { /* errors surface per-item in the list on refetch */ }
	}

	return (
		<>
			<div className="min-h-[200px]">
				{isLoading ? (
					<div className="grid place-items-center py-8">
						<Spinner size="sm" />
					</div>
				) : isError ? (
					<p className="py-6 text-sm text-(--color-destructive)">
						{t('settings.integrations.avitoImport.loadError')}
					</p>
				) : items.length === 0 ? (
					<p className="py-6 text-sm text-(--color-muted-foreground)">
						{t('settings.integrations.avitoImport.empty')}
					</p>
				) : (
					<ul className="flex max-h-[45vh] flex-col gap-2 overflow-y-auto">
						{items.map((item) => {
							const imported = !!item.importedPropertyId
							return (
								<li
									key={item.avitoItemId}
									className="flex items-center gap-3 rounded-(--radius-md) border border-(--color-border) p-2"
								>
									<Checkbox
										checked={selected.has(item.avitoItemId)}
										disabled={imported}
										onCheckedChange={() => toggle(item.avitoItemId)}
									/>
									{item.photos?.[0] ? (
										<img src={item.photos[0]} alt="" className="size-12 shrink-0 rounded-(--radius-sm) object-cover" />
									) : (
										<div className="size-12 shrink-0 rounded-(--radius-sm) bg-(--color-muted)" />
									)}
									<div className="min-w-0 flex-1">
										<p className="truncate text-sm font-medium">{item.title}</p>
										{item.address && (
											<p className="truncate text-xs text-(--color-muted-foreground)">{item.address}</p>
										)}
										<div className="mt-1 flex items-center gap-2">
											{item.price && <span className="text-xs">{item.price} {item.currency ?? ''}</span>}
											<Badge size="sm" variant="secondary">
												{t(`settings.integrations.avitoImport.status.${item.status}`)}
											</Badge>
										</div>
									</div>
									{imported && (
										<Badge size="sm" variant="success">
											{t('settings.integrations.avitoImport.alreadyImported')}
										</Badge>
									)}
								</li>
							)
						})}
					</ul>
				)}
			</div>

			<DialogFooter>
				<Button size="sm" onClick={() => void handleImport()} disabled={selected.size === 0 || isImporting}>
					{isImporting
						? t('settings.integrations.avitoImport.importing')
						: t('settings.integrations.avitoImport.importSelected', { count: selected.size })}
				</Button>
			</DialogFooter>
		</>
	)
}

// ── URL tab ───────────────────────────────────────────────────────────────────

type UrlStep = 'input' | 'processing' | 'review'

function UrlTab({ connectionId, onClose }: { connectionId?: string; onClose: () => void }) {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [step, setStep]           = useState<UrlStep>('input')
	const [sourceUrl, setSourceUrl] = useState('')
	const [importId, setImportId]   = useState<string | null>(null)
	const [ownerId, setOwnerId]     = useState('__me__')
	const [urlError, setUrlError]   = useState('')
	const [completeError, setCompleteError] = useState('')
	const [manualFields, setManualFields] = useState({ type: '', address: '', city: '' })
	const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

	const [startImport, { isLoading: isStarting }] = useStartImportMutation()
	const [completeImport, { isLoading: isCompleting }] = useCompleteImportMutation()
	const { data: importData, refetch } = useGetImportQuery(importId!, { skip: !importId || step !== 'processing' })
	const { data: subAccounts } = useListSubAccountsQuery()
	const { data: me } = useGetMeQuery()

	useEffect(() => {
		if (step !== 'processing' || !importId) return
		pollingRef.current = setInterval(() => { void refetch() }, 2000)
		return () => { if (pollingRef.current) clearInterval(pollingRef.current) }
	}, [step, importId, refetch])

	useEffect(() => {
		if (!importData || importData.status === 'VALIDATING') return
		if (pollingRef.current) clearInterval(pollingRef.current)

		// Pre-fill editable fields from whatever the backend parsed — no infinite loop
		// (we never set importData here, only local UI state).
		/* eslint-disable react-hooks/set-state-in-effect */
		if (importData.status === 'MISSING_DATA' && importData.parsedPayloadJson) {
			try {
				const parsed = JSON.parse(importData.parsedPayloadJson) as Record<string, unknown>
				setManualFields({
					type:    typeof parsed.type    === 'string' ? parsed.type    : '',
					address: typeof parsed.address === 'string' ? parsed.address : '',
					city:    typeof parsed.city    === 'string' ? parsed.city    : '',
				})
			} catch { /* ignore */ }
		}
		setStep('review')
		/* eslint-enable react-hooks/set-state-in-effect */
	}, [importData])

	async function handleStart() {
		setUrlError('')
		if (!sourceUrl.trim()) { setUrlError(t('settings.integrations.avitoImport.urlRequired')); return }
		try {
			const res = await startImport({ sourceUrl: sourceUrl.trim(), connectionId }).unwrap()
			setImportId(res.id)
			setStep('processing')
		} catch (err) {
			const e = err as { data?: { message?: string | string[] }; status?: number }
			const msg = Array.isArray(e?.data?.message) ? e.data.message.join('; ') : (e?.data?.message ?? t('settings.integrations.avitoImport.startError'))
			setUrlError(`[${e?.status ?? '?'}] ${String(msg)}`)
		}
	}

	async function handleComplete(imp: PublicationImport) {
		setCompleteError('')
		try {
			const resolvedOwnerId = ownerId === '__me__' ? me?.id : ownerId
			// For MISSING_DATA we send the manual fields so the backend can merge them
			const extraPayload: Record<string, string> = {}
			if (imp.status === 'MISSING_DATA') {
				if (manualFields.type) extraPayload.type = manualFields.type
				if (manualFields.address.trim()) extraPayload.address = manualFields.address.trim()
				if (manualFields.city.trim()) extraPayload.city = manualFields.city.trim()
			}
			const result = await completeImport({
				id:   imp.id,
				body: { payloadJson: JSON.stringify(extraPayload), ownerId: resolvedOwnerId },
			}).unwrap()
			onClose()
			if (result.importedPropertyId) {
				void navigate({ to: '/properties/$propertyId', params: { propertyId: result.importedPropertyId } })
			}
		} catch (err) {
			const e = err as { data?: { message?: string | string[] } }
			const msg = Array.isArray(e?.data?.message) ? e.data.message.join('; ') : (e?.data?.message ?? 'Ошибка импорта')
			setCompleteError(String(msg))
		}
	}

	function reset() {
		if (pollingRef.current) clearInterval(pollingRef.current)
		setStep('input')
		setSourceUrl('')
		setImportId(null)
		setOwnerId('__me__')
		setUrlError('')
		setManualFields({ type: '', address: '', city: '' })
	}

	const isMissingData = step === 'review' && importData?.status === 'MISSING_DATA'
	const missingDataValid = !isMissingData || (!!manualFields.type && !!manualFields.city.trim() && !!manualFields.address.trim())

	return (
		<>
			<div className="flex flex-col gap-4 min-h-[200px]">
				{step === 'input' && (
					<div className="flex flex-col gap-1.5">
						<Label>{t('settings.integrations.avitoImport.urlLabel')}</Label>
						<Input
							placeholder="https://www.cian.ru/rent/flat/123456/"
							value={sourceUrl}
							onChange={(e) => setSourceUrl(e.target.value)}
							onKeyDown={(e) => { if (e.key === 'Enter') void handleStart() }}
						/>
						{urlError && <p className="text-xs text-(--color-destructive)">{urlError}</p>}
					</div>
				)}

				{step === 'processing' && (
					<div className="flex flex-col items-center gap-3 py-8">
						<Spinner size="sm" />
						<p className="text-sm text-(--color-muted-foreground)">{t('settings.integrations.avitoImport.processing')}</p>
					</div>
				)}

				{step === 'review' && importData && (
					<div className="flex flex-col gap-4">
						{importData.status === 'FAILED' ? (
							<Alert variant="destructive">
								<XCircleIcon className="size-4" />
								<AlertDescription>{importData.error ?? t('settings.integrations.avitoImport.failed')}</AlertDescription>
							</Alert>
						) : (
							<>
								<Alert variant={isMissingData ? 'warning' : 'success'}>
									{isMissingData
										? <AlertCircleIcon className="size-4" />
										: <CheckCircle2Icon className="size-4" />}
									<AlertDescription>
										{isMissingData
											? t('settings.integrations.avitoImport.missingData')
											: t('settings.integrations.avitoImport.ready')}
										{/* Why the automatic parse gave nothing (expired Avito
										    token, unknown item…) — the backend keeps it on the row. */}
										{isMissingData && importData.error && (
											<p className="mt-1 text-xs opacity-80">{importData.error}</p>
										)}
									</AlertDescription>
								</Alert>

								{isMissingData && (
									<div className="flex flex-col gap-3">
										<div className="flex flex-col gap-1.5">
											<Label>{t('settings.integrations.avitoImport.typeLabel')}</Label>
											<Select value={manualFields.type} onValueChange={(v) => setManualFields((f) => ({ ...f, type: v }))}>
												<SelectTrigger size="md" width="full">
													<SelectValue placeholder={t('settings.integrations.avitoImport.typePlaceholder')} />
												</SelectTrigger>
												<SelectContent>
													{CREATE_PROPERTY_TYPES.map((value) => (
														<SelectItem key={value} value={value}>{t(`properties.types.${value}`)}</SelectItem>
													))}
												</SelectContent>
											</Select>
										</div>
										<div className="flex flex-col gap-1.5">
											<Label>{t('settings.integrations.avitoImport.cityLabel')}</Label>
											<Input
												placeholder={t('settings.integrations.avitoImport.cityPlaceholder')}
												value={manualFields.city}
												onChange={(e) => setManualFields((f) => ({ ...f, city: e.target.value }))}
											/>
										</div>
										<div className="flex flex-col gap-1.5">
											<Label>{t('settings.integrations.avitoImport.addressLabel')}</Label>
											<Input
												placeholder={t('settings.integrations.avitoImport.addressPlaceholder')}
												value={manualFields.address}
												onChange={(e) => setManualFields((f) => ({ ...f, address: e.target.value }))}
											/>
										</div>
									</div>
								)}

								{(subAccounts ?? []).length > 0 && (
									<div className="flex flex-col gap-1.5">
										<Label>{t('settings.integrations.avitoImport.ownerLabel')}</Label>
										<Select value={ownerId} onValueChange={setOwnerId}>
											<SelectTrigger>
												<SelectValue placeholder={t('settings.integrations.avitoImport.ownerPlaceholder')} />
											</SelectTrigger>
											<SelectContent>
												<SelectItem value="__me__">{t('settings.integrations.avitoImport.ownerMe')}</SelectItem>
												{(subAccounts ?? []).map((sa) => (
													<SelectItem key={sa.id} value={sa.id}>{sa.fullName}</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								)}
							</>
						)}
					</div>
				)}
			</div>

			<DialogFooter>
				{completeError && (
					<p className="w-full text-xs text-(--color-destructive)">{completeError}</p>
				)}
				{step === 'input' && (
					<Button size="sm" onClick={() => void handleStart()} disabled={isStarting}>
						{isStarting ? <Spinner size="sm" /> : t('settings.integrations.avitoImport.next')}
					</Button>
				)}
				{step === 'review' && importData?.status === 'FAILED' && (
					<Button size="sm" variant="secondary" onClick={reset}>
						{t('settings.integrations.avitoImport.retry')}
					</Button>
				)}
				{step === 'review' && importData && importData.status !== 'FAILED' && (
					<Button size="sm" onClick={() => void handleComplete(importData)} disabled={isCompleting || !missingDataValid}>
						{isCompleting ? <Spinner size="sm" /> : t('settings.integrations.avitoImport.import')}
					</Button>
				)}
			</DialogFooter>
		</>
	)
}

// ── Main dialog ───────────────────────────────────────────────────────────────

export function AvitoImportDialog({ connectionId, open, onOpenChange }: AvitoImportDialogProps) {
	const { t } = useTranslation()
	const defaultTab = connectionId ? 'cabinet' : 'url'

	return (
		<Dialog open={open} onOpenChange={onOpenChange} disablePointerDismissal>
			<DialogContent showCloseButton={false} className="max-w-2xl">
				<DialogHeader
					title={t('settings.integrations.avitoImport.title')}
					subtitle={t('settings.integrations.avitoImport.description')}
				/>

				<Tabs defaultValue={defaultTab}>
					<TabsList>
						{connectionId && (
							<TabsTrigger value="cabinet">
								{t('settings.integrations.avitoImport.tabCabinet')}
							</TabsTrigger>
						)}
						<TabsTrigger value="url">
							{t('settings.integrations.avitoImport.tabUrl')}
						</TabsTrigger>
					</TabsList>

					{connectionId && (
						<TabsContent value="cabinet">
							<CabinetTab connectionId={connectionId} open={open} onClose={() => onOpenChange(false)} />
						</TabsContent>
					)}

					<TabsContent value="url">
						<UrlTab connectionId={connectionId} onClose={() => onOpenChange(false)} />
					</TabsContent>
				</Tabs>
			</DialogContent>
		</Dialog>
	)
}
